import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Store, Plus, Eye, Loader2, Building2, Hash, Car, Wrench, CheckCircle, Clock, XCircle, ShoppingCart,
  AlertTriangle, FileEdit, ChevronDown, ChevronUp, Upload, File, Bell, AlertCircle, X, Save, Calendar, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguage } from '../context/LanguageContext';
import { useRegion } from '../context/RegionContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Edit request reasons translation
const EDIT_REASONS = {
  price_update: { ar: 'تحديث السعر', en: 'Price Update' },
  wrong_info: { ar: 'معلومات خاطئة', en: 'Wrong Information' },
  missing_details: { ar: 'تفاصيل ناقصة', en: 'Missing Details' },
  image_quality: { ar: 'جودة الصور', en: 'Image Quality' },
  description_update: { ar: 'تحديث الوصف', en: 'Description Update' },
  category_change: { ar: 'تغيير التصنيف', en: 'Category Change' },
  other: { ar: 'أخرى', en: 'Other' }
};

export default function Stores() {
  const { language } = useLanguage();
  const { formatPrice } = useRegion();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'rejected', or 'requests'
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [editingStore, setEditingStore] = useState(null);
  const [newFiles, setNewFiles] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // Edit requests state
  const [editRequests, setEditRequests] = useState([]);
  const [editRequestsCount, setEditRequestsCount] = useState(0);
  const [loadingEditRequests, setLoadingEditRequests] = useState(false);
  
  // Edit request modal state
  const [selectedEditRequest, setSelectedEditRequest] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Rejection reason modal state
  const [selectedRejectedStore, setSelectedRejectedStore] = useState(null);
  
  // Filter dropdown state
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Store types for display
  const storeTypes = [
    { id: 'real_estate', name: 'تاجر عقارات', nameEn: 'Real Estate', icon: Building2, color: 'from-blue-500 to-cyan-500' },
    { id: 'phone_numbers', name: 'أرقام الهواتف', nameEn: 'Phone Numbers', icon: Hash, color: 'from-[#1E9BD9] to-[#0D7AB5]' },
    { id: 'car_plates', name: 'أرقام السيارات', nameEn: 'Car Plates', icon: Car, color: 'from-amber-500 to-yellow-500' },
    { id: 'spare_parts', name: 'قطع غيار', nameEn: 'Spare Parts', icon: Wrench, color: 'from-orange-500 to-red-500' },
    { id: 'auto', name: 'تاجر سيارات', nameEn: 'Auto Dealer', icon: Car, color: 'from-green-500 to-emerald-500' },
    { id: 'ecommerce', name: 'تجارة إلكترونية', nameEn: 'E-Commerce', icon: ShoppingCart, color: 'from-cyan-500 to-blue-500' }
  ];

  const fetchStores = async () => {
    const token = localStorage.getItem('business_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStores(response.data);
    } catch (error) {
      console.error('Failed to fetch stores:', error);
      setStores([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch edit requests
  const fetchEditRequests = async () => {
    const token = localStorage.getItem('business_token');
    if (!token) return;

    setLoadingEditRequests(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/merchant/edit-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditRequests(response.data || []);
      setEditRequestsCount(response.data.filter(r => r.status === 'pending').length);
    } catch (error) {
      console.error('Failed to fetch edit requests:', error);
      setEditRequests([]);
    } finally {
      setLoadingEditRequests(false);
    }
  };

  // Mark edit request as resolved
  const resolveEditRequest = async (requestId) => {
    const token = localStorage.getItem('business_token');
    try {
      await axios.put(`${BACKEND_URL}/api/merchant/edit-requests/${requestId}/resolve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'ar' ? 'تم حل الطلب بنجاح' : 'Request resolved successfully');
      setSelectedEditRequest(null);
      fetchEditRequests();
      fetchStores();
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل في حل الطلب' : 'Failed to resolve request');
    }
  };

  // Open edit request modal
  const openEditRequestModal = async (request) => {
    // Fetch store details for editing
    const token = localStorage.getItem('business_token');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/stores/${request.store_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditFormData({
        name: response.data.name || '',
        nameEn: response.data.nameEn || '',
        description: response.data.description || '',
        descriptionEn: response.data.descriptionEn || ''
      });
      setSelectedEditRequest({ ...request, storeData: response.data });
    } catch (error) {
      // If can't fetch store, just show the request
      setEditFormData({});
      setSelectedEditRequest(request);
    }
  };

  // Save store edits from edit request
  const saveStoreEdits = async () => {
    if (!selectedEditRequest) return;
    
    const token = localStorage.getItem('business_token');
    setSavingEdit(true);
    
    try {
      // Update store with new data
      await axios.put(`${BACKEND_URL}/api/stores/${selectedEditRequest.store_id}`, editFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Mark request as resolved
      await axios.put(`${BACKEND_URL}/api/merchant/edit-requests/${selectedEditRequest.id}/resolve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(language === 'ar' ? 'تم حفظ التعديلات وحل الطلب بنجاح' : 'Edits saved and request resolved successfully');
      setSelectedEditRequest(null);
      setEditFormData({});
      fetchEditRequests();
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل في حفظ التعديلات' : 'Failed to save edits'));
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    fetchStores();
    fetchEditRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTypeInfo = (typeId) => {
    return storeTypes.find(t => t.id === typeId) || storeTypes[0];
  };

  // Separate stores by status
  const activeStores = stores.filter(s => s.status === 'active' && s.verified);
  const rejectedStores = stores.filter(s => s.status === 'rejected');
  const suspendedStores = stores.filter(s => s.status === 'suspended');
  const requestStores = stores.filter(s => s.status !== 'active' && s.status !== 'rejected' && s.status !== 'suspended');

  // Get status info
  const getStatusInfo = (store) => {
    if (store.status === 'rejected') {
      return {
        label: language === 'ar' ? 'مرفوض' : 'Rejected',
        color: 'bg-red-500',
        textColor: 'text-red-600',
        bgColor: 'bg-red-50',
        bgColorDark: 'bg-red-500/10',
        borderColor: 'border-red-200',
        icon: XCircle
      };
    } else if (store.status === 'modification_required') {
      return {
        label: language === 'ar' ? 'مطلوب تعديل' : 'Modification Required',
        color: 'bg-orange-500',
        textColor: 'text-orange-600',
        bgColor: 'bg-orange-50',
        bgColorDark: 'bg-orange-500/10',
        borderColor: 'border-orange-200',
        icon: FileEdit
      };
    } else {
      return {
        label: language === 'ar' ? 'قيد المراجعة' : 'Under Review',
        color: 'bg-yellow-500',
        textColor: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        bgColorDark: 'bg-yellow-500/10',
        borderColor: 'border-yellow-200',
        icon: Clock
      };
    }
  };

  // Handle file change for modifications
  const handleFileChange = (storeId, fileType, file) => {
    setNewFiles(prev => ({
      ...prev,
      [storeId]: {
        ...prev[storeId],
        [fileType]: file
      }
    }));
  };

  // Submit modifications
  const submitModifications = async (storeId) => {
    const token = localStorage.getItem('business_token');
    const files = newFiles[storeId];
    
    if (!files || Object.keys(files).length === 0) {
      toast.error(language === 'ar' ? 'الرجاء رفع الملفات المطلوبة' : 'Please upload required files');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(files).forEach(([key, file]) => {
        formData.append(key, file);
      });

      await axios.put(`${BACKEND_URL}/api/stores/${storeId}/resubmit`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(language === 'ar' ? 'تم إرسال التعديلات بنجاح' : 'Modifications submitted successfully');
      setNewFiles(prev => ({ ...prev, [storeId]: {} }));
      setEditingStore(null);
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل إرسال التعديلات' : 'Failed to submit modifications'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center cgm-page`}>
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-[#38BDF8]/20 border-t-[#38BDF8] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen overflow-x-hidden cgm-page`}>
      {/* Header — AI-Grade Animated Hero */}
      <section className="cgm-hero py-6 sm:py-8">
        <div className="cgm-hero-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-white">
            <div className="cgm-reveal">
              <div className="flex items-center gap-3 mb-2">
                <div className="cgm-icon-glow cgm-breathe !p-2.5">
                  <Store className="h-5 w-5 text-white" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-black tracking-tight" data-testid="stores-title">
                  {language === 'ar' ? 'متاجري' : 'My Stores'}
                </h1>
              </div>
              <p className="text-white/80 text-sm sm:text-base">
                {language === 'ar' ? 'إدارة وإنشاء متاجرك بذكاء' : 'Smart store management & creation'}
              </p>
            </div>
            <button 
              onClick={() => navigate('/stores/create')}
              className="cgm-cta-primary w-full sm:w-auto cgm-reveal !bg-white !text-slate-900 !shadow-xl hover:!bg-slate-50"
              style={{ animationDelay: '150ms', background: '#ffffff' }}
              data-testid="create-store-btn"
            >
              <Plus className="h-5 w-5 text-[#F97316]" />
              <span className="font-black" style={{ background: 'linear-gradient(90deg, #0EA5E9, #F97316)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
                {language === 'ar' ? 'إنشاء متجر جديد' : 'Create New Store'}
              </span>
            </button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-hidden">
        
        {/* Filter Dropdown - Clean Design */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          {/* Dropdown Filter */}
          <div className="relative">
            <button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all min-w-[200px] ${
                isDarkMode 
                  ? 'bg-[#131B2C] border-white/10 hover:border-[#38BDF8]/50' 
                  : 'bg-white border-black/10 hover:border-[#38BDF8]/50 shadow-sm'
              }`}
              data-testid="filter-dropdown-btn"
            >
              {/* Current Selection Icon & Label */}
              {activeTab === 'active' && <Store className="h-4 w-4 text-emerald-500" />}
              {activeTab === 'rejected' && <XCircle className="h-4 w-4 text-red-500" />}
              {activeTab === 'requests' && <Clock className="h-4 w-4 text-amber-500" />}
              {activeTab === 'edit_requests' && <FileEdit className="h-4 w-4 text-[#38BDF8]" />}
              {activeTab === 'suspended' && <AlertTriangle className="h-4 w-4 text-gray-500" />}
              
              <span className={`flex-1 text-sm font-medium text-start ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {activeTab === 'active' && (language === 'ar' ? 'المتاجر الفعالة' : 'Active Stores')}
                {activeTab === 'rejected' && (language === 'ar' ? 'المتاجر المرفوضة' : 'Rejected Stores')}
                {activeTab === 'requests' && (language === 'ar' ? 'طلبات التفعيل' : 'Activation Requests')}
                {activeTab === 'edit_requests' && (language === 'ar' ? 'طلبات التعديل' : 'Edit Requests')}
                {activeTab === 'suspended' && (language === 'ar' ? 'المتاجر الموقوفة' : 'Suspended Stores')}
              </span>
              
              {/* Count Badge */}
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                activeTab === 'active' ? 'bg-emerald-500/20 text-emerald-500' :
                activeTab === 'rejected' ? 'bg-red-500/20 text-red-500' :
                activeTab === 'requests' ? 'bg-amber-500/20 text-amber-500' :
                activeTab === 'edit_requests' ? 'bg-[#38BDF8]/20 text-[#38BDF8]' :
                'bg-gray-500/20 text-gray-500'
              }`}>
                {activeTab === 'active' && activeStores.length}
                {activeTab === 'rejected' && rejectedStores.length}
                {activeTab === 'requests' && requestStores.length}
                {activeTab === 'edit_requests' && editRequestsCount}
                {activeTab === 'suspended' && suspendedStores.length}
              </span>
              
              <ChevronDown className={`h-4 w-4 transition-transform ${showFilterDropdown ? 'rotate-180' : ''} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            </button>
            
            {/* Dropdown Menu */}
            {showFilterDropdown && (
              <div className={`absolute top-full mt-2 start-0 w-64 rounded-xl border shadow-xl z-50 overflow-hidden ${
                isDarkMode 
                  ? 'bg-[#131B2C] border-white/10' 
                  : 'bg-white border-black/5'
              }`}>
                {/* Active Stores */}
                <button
                  onClick={() => { setActiveTab('active'); setShowFilterDropdown(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                    activeTab === 'active' 
                      ? 'bg-emerald-500/10' 
                      : isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                  }`}
                >
                  <Store className={`h-4 w-4 ${activeTab === 'active' ? 'text-emerald-500' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  <span className={`flex-1 text-sm text-start ${activeTab === 'active' ? 'text-emerald-500 font-semibold' : isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {language === 'ar' ? 'المتاجر الفعالة' : 'Active Stores'}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-500">
                    {activeStores.length}
                  </span>
                </button>
                
                {/* Rejected Stores */}
                <button
                  onClick={() => { setActiveTab('rejected'); setShowFilterDropdown(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                    activeTab === 'rejected' 
                      ? 'bg-red-500/10' 
                      : isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                  }`}
                >
                  <XCircle className={`h-4 w-4 ${activeTab === 'rejected' ? 'text-red-500' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  <span className={`flex-1 text-sm text-start ${activeTab === 'rejected' ? 'text-red-500 font-semibold' : isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {language === 'ar' ? 'المتاجر المرفوضة' : 'Rejected Stores'}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-500/20 text-red-500">
                    {rejectedStores.length}
                  </span>
                </button>
                
                {/* Activation Requests */}
                <button
                  onClick={() => { setActiveTab('requests'); setShowFilterDropdown(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                    activeTab === 'requests' 
                      ? 'bg-amber-500/10' 
                      : isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                  }`}
                >
                  <Clock className={`h-4 w-4 ${activeTab === 'requests' ? 'text-amber-500' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  <span className={`flex-1 text-sm text-start ${activeTab === 'requests' ? 'text-amber-500 font-semibold' : isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {language === 'ar' ? 'طلبات التفعيل' : 'Activation Requests'}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-500/20 text-amber-500">
                    {requestStores.length}
                  </span>
                </button>
                
                {/* Edit Requests */}
                <button
                  onClick={() => { setActiveTab('edit_requests'); setShowFilterDropdown(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                    activeTab === 'edit_requests' 
                      ? 'bg-[#38BDF8]/10' 
                      : isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                  }`}
                >
                  <FileEdit className={`h-4 w-4 ${activeTab === 'edit_requests' ? 'text-[#38BDF8]' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  <span className={`flex-1 text-sm text-start ${activeTab === 'edit_requests' ? 'text-[#38BDF8] font-semibold' : isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {language === 'ar' ? 'طلبات التعديل' : 'Edit Requests'}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#38BDF8]/20 text-[#38BDF8]">
                    {editRequestsCount}
                  </span>
                </button>
                
                {/* Suspended Stores */}
                <button
                  onClick={() => { setActiveTab('suspended'); setShowFilterDropdown(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                    activeTab === 'suspended' 
                      ? 'bg-gray-500/10' 
                      : isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                  }`}
                >
                  <AlertTriangle className={`h-4 w-4 ${activeTab === 'suspended' ? 'text-gray-500' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  <span className={`flex-1 text-sm text-start ${activeTab === 'suspended' ? 'text-gray-500 font-semibold' : isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {language === 'ar' ? 'المتاجر الموقوفة' : 'Suspended Stores'}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-gray-500/20 text-gray-500">
                    {suspendedStores.length}
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Requests Tab Content */}
        {activeTab === 'requests' && (
          <section>
            {requestStores.length === 0 ? (
              <div className="cgm-ai-card text-center !py-16">
                <div className="cgm-icon-glow !w-16 !h-16 !p-4 inline-flex mb-4 cgm-float"><Clock className="h-7 w-7 text-white" /></div>
                <p className="text-lg mb-2 font-black cgm-text-gradient-animated" style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {language === 'ar' ? 'لا توجد طلبات حالياً' : 'No pending requests'}
                </p>
                <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {language === 'ar' ? 'أنشئ متجراً جديداً وسيظهر هنا' : 'Create a new store and it will appear here'}
                </p>
                <Button 
                  onClick={() => navigate('/stores/create')}
                  className="bg-[#1E9BD9] hover:bg-[#0369A1] text-white"
                >
                  <Plus className="h-5 w-5 me-2" />
                  {language === 'ar' ? 'إنشاء متجر جديد' : 'Create New Store'}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {requestStores.map((store) => {
                  const typeInfo = getTypeInfo(store.type);
                  const statusInfo = getStatusInfo(store);
                  const StatusIcon = statusInfo.icon;
                  const isExpanded = expandedRequest === store.id;
                  const isEditing = editingStore === store.id;

                  return (
                    <div 
                      key={store.id} 
                      className={`rounded-xl border-2 ${statusInfo.borderColor} overflow-hidden ${
                        isDarkMode ? 'bg-[#131B2C]' : 'bg-white shadow-sm'
                      }`}
                      data-testid={`request-${store.id}`}
                    >
                      {/* Request Header */}
                      <div 
                        className={`p-4 sm:p-5 cursor-pointer ${
                          isDarkMode ? statusInfo.bgColorDark || 'bg-white/5' : statusInfo.bgColor
                        }`}
                        onClick={() => setExpandedRequest(isExpanded ? null : store.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${typeInfo.color} flex items-center justify-center`}>
                              <typeInfo.icon className="h-6 w-6 text-white" />
                            </div>
                            <div>
                              <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                                {language === 'ar' ? store.name : (store.nameEn || store.name)}
                              </h3>
                              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {language === 'ar' ? typeInfo.name : typeInfo.nameEn}
                              </p>
                              {store.store_uid && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(store.store_uid);
                                    toast.success(language === 'ar' ? 'تم نسخ الرقم التعريفي' : 'Store ID copied');
                                  }}
                                  className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-md text-[10px] font-mono tracking-wider transition-colors ${
                                    isDarkMode
                                      ? 'bg-white/5 hover:bg-white/10 text-gray-300 border border-white/10'
                                      : 'bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200'
                                  }`}
                                  data-testid={`store-card-uid-${store.id}`}
                                  title={language === 'ar' ? 'انقر للنسخ' : 'Click to copy'}
                                >
                                  <span className="opacity-60">{language === 'ar' ? 'ID:' : 'ID:'}</span>
                                  <span dir="ltr">{store.store_uid}</span>
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-3 py-1.5 ${statusInfo.color} text-white text-xs font-bold rounded-full flex items-center gap-1.5`}>
                              <StatusIcon className="h-3.5 w-3.5" />
                              {statusInfo.label}
                            </span>
                            {isExpanded ? (
                              <ChevronUp className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                            ) : (
                              <ChevronDown className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className={`p-4 sm:p-5 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
                          {/* Rejection Reason */}
                          {store.status === 'rejected' && store.rejection_reason && (
                            <div className={`mb-4 p-4 rounded-xl border ${
                              isDarkMode ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'
                            }`}>
                              <div className="flex items-start gap-3">
                                <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className={`font-bold mb-1 ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
                                    {language === 'ar' ? 'سبب الرفض:' : 'Rejection Reason:'}
                                  </p>
                                  <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{store.rejection_reason}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Modification Request */}
                          {store.status === 'modification_required' && (
                            <div className={`mb-4 p-4 rounded-xl border ${
                              isDarkMode ? 'bg-orange-500/10 border-orange-500/30' : 'bg-orange-50 border-orange-200'
                            }`}>
                              <div className="flex items-start gap-3">
                                <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <p className={`font-bold mb-1 ${isDarkMode ? 'text-orange-400' : 'text-orange-700'}`}>
                                    {language === 'ar' ? 'التعديلات المطلوبة:' : 'Required Modifications:'}
                                  </p>
                                  <p className={`text-sm mb-4 ${isDarkMode ? 'text-orange-300' : 'text-orange-600'}`}>{store.modification_message || (language === 'ar' ? 'يرجى تعديل المستندات المرفقة' : 'Please update the attached documents')}</p>
                                  
                                  {!isEditing ? (
                                    <Button
                                      onClick={(e) => { e.stopPropagation(); setEditingStore(store.id); }}
                                      className="bg-orange-500 hover:bg-orange-600 text-white"
                                      size="sm"
                                    >
                                      <FileEdit className="h-4 w-4 me-2" />
                                      {language === 'ar' ? 'تعديل الملفات' : 'Edit Files'}
                                    </Button>
                                  ) : (
                                    <div className={`space-y-4 mt-4 p-4 rounded-lg border ${
                                      isDarkMode ? 'bg-[#0D1421] border-orange-500/30' : 'bg-white border-orange-200'
                                    }`}>
                                      <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {language === 'ar' ? 'ارفع الملفات المعدلة:' : 'Upload modified files:'}
                                      </p>
                                      
                                      {/* License Upload */}
                                      <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-2">
                                          {language === 'ar' ? 'الرخصة التجارية' : 'Commercial License'}
                                        </label>
                                        <div className="flex items-center gap-3">
                                          <input
                                            type="file"
                                            accept="image/*,.pdf"
                                            onChange={(e) => handleFileChange(store.id, 'license', e.target.files[0])}
                                            className="hidden"
                                            id={`license-${store.id}`}
                                          />
                                          <label 
                                            htmlFor={`license-${store.id}`}
                                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors"
                                          >
                                            <Upload className="h-4 w-4" />
                                            <span className="text-sm">{language === 'ar' ? 'اختر ملف' : 'Choose file'}</span>
                                          </label>
                                          {newFiles[store.id]?.license && (
                                            <span className="text-sm text-green-600 flex items-center gap-1">
                                              <File className="h-4 w-4" />
                                              {newFiles[store.id].license.name}
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      {/* Authorization Upload (if not owner) */}
                                      {store.is_owner === false && (
                                        <>
                                          <div>
                                            <label className="block text-sm font-medium text-gray-600 mb-2">
                                              {language === 'ar' ? 'رسالة التخويل' : 'Authorization Letter'}
                                            </label>
                                            <div className="flex items-center gap-3">
                                              <input
                                                type="file"
                                                accept="image/*,.pdf"
                                                onChange={(e) => handleFileChange(store.id, 'authorization', e.target.files[0])}
                                                className="hidden"
                                                id={`auth-${store.id}`}
                                              />
                                              <label 
                                                htmlFor={`auth-${store.id}`}
                                                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors"
                                              >
                                                <Upload className="h-4 w-4" />
                                                <span className="text-sm">{language === 'ar' ? 'اختر ملف' : 'Choose file'}</span>
                                              </label>
                                              {newFiles[store.id]?.authorization && (
                                                <span className="text-sm text-green-600 flex items-center gap-1">
                                                  <File className="h-4 w-4" />
                                                  {newFiles[store.id].authorization.name}
                                                </span>
                                              )}
                                            </div>
                                          </div>

                                          <div>
                                            <label className="block text-sm font-medium text-gray-600 mb-2">
                                              {language === 'ar' ? 'رسالة اعتماد التوقيع' : 'Signature Approval'}
                                            </label>
                                            <div className="flex items-center gap-3">
                                              <input
                                                type="file"
                                                accept="image/*,.pdf"
                                                onChange={(e) => handleFileChange(store.id, 'signatureApproval', e.target.files[0])}
                                                className="hidden"
                                                id={`sig-${store.id}`}
                                              />
                                              <label 
                                                htmlFor={`sig-${store.id}`}
                                                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition-colors"
                                              >
                                                <Upload className="h-4 w-4" />
                                                <span className="text-sm">{language === 'ar' ? 'اختر ملف' : 'Choose file'}</span>
                                              </label>
                                              {newFiles[store.id]?.signatureApproval && (
                                                <span className="text-sm text-green-600 flex items-center gap-1">
                                                  <File className="h-4 w-4" />
                                                  {newFiles[store.id].signatureApproval.name}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </>
                                      )}

                                      <div className="flex gap-3 pt-2">
                                        <Button
                                          onClick={() => submitModifications(store.id)}
                                          disabled={submitting}
                                          className="bg-green-600 hover:bg-green-700 text-white"
                                          size="sm"
                                        >
                                          {submitting ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                          ) : (
                                            <>
                                              <CheckCircle className="h-4 w-4 me-2" />
                                              {language === 'ar' ? 'إرسال التعديلات' : 'Submit'}
                                            </>
                                          )}
                                        </Button>
                                        <Button
                                          onClick={() => { setEditingStore(null); setNewFiles(prev => ({ ...prev, [store.id]: {} })); }}
                                          variant="outline"
                                          size="sm"
                                        >
                                          {language === 'ar' ? 'إلغاء' : 'Cancel'}
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Pending Review Info */}
                          {store.status === 'pending' && (
                            <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
                              <div className="flex items-start gap-3">
                                <Clock className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="font-bold text-yellow-700 mb-1">
                                    {language === 'ar' ? 'طلبك قيد المراجعة' : 'Your request is under review'}
                                  </p>
                                  <p className="text-yellow-600 text-sm">
                                    {language === 'ar' 
                                      ? 'سيتم مراجعة طلبك والرد عليك خلال 48 ساعة كحد أقصى' 
                                      : 'Your request will be reviewed and you will receive a response within 48 hours maximum'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Store Details Summary */}
                          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-[#0D1421]' : 'bg-gray-50'}`}>
                              <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                {language === 'ar' ? 'نوع النشاط' : 'Business Type'}
                              </p>
                              <p className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                                {store.business_type === 'company' 
                                  ? (language === 'ar' ? 'شركة' : 'Company')
                                  : (language === 'ar' ? 'شخصي' : 'Personal')}
                              </p>
                            </div>
                            <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-[#0D1421]' : 'bg-gray-50'}`}>
                              <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                {language === 'ar' ? 'تاريخ الطلب' : 'Request Date'}
                              </p>
                              <p className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                                {new Date(store.created_at).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US')}
                              </p>
                            </div>
                            {store.business_type === 'company' && (
                              <>
                                <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-[#0D1421]' : 'bg-gray-50'}`}>
                                  <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                    {language === 'ar' ? 'الرخصة' : 'License'}
                                  </p>
                                  <p className={`font-semibold text-sm ${store.license_document ? 'text-green-500' : 'text-red-500'}`}>
                                    {store.license_document 
                                      ? (language === 'ar' ? 'مرفقة' : 'Attached')
                                      : (language === 'ar' ? 'غير مرفقة' : 'Not attached')}
                                  </p>
                                </div>
                                <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-[#0D1421]' : 'bg-gray-50'}`}>
                                  <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                    {language === 'ar' ? 'صاحب المؤسسة' : 'Owner'}
                                  </p>
                                  <p className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                                    {store.is_owner 
                                      ? (language === 'ar' ? 'نعم' : 'Yes')
                                      : (language === 'ar' ? 'لا - مُفوض' : 'No - Authorized')}
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Active Stores Tab Content */}
        {activeTab === 'active' && (
          <section>
            {activeStores.length === 0 ? (
              <div className="cgm-ai-card text-center !py-16">
                <div className="cgm-icon-glow !w-16 !h-16 !p-4 inline-flex mb-4 cgm-float"><Store className="h-7 w-7 text-white" /></div>
                <p className="text-lg mb-2 font-black cgm-text-gradient-animated" style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {language === 'ar' ? 'لا توجد متاجر فعالة حالياً' : 'No active stores yet'}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {language === 'ar' ? 'ستظهر هنا بعد الموافقة على طلباتك' : 'They will appear here after approval'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 cgm-reveal-stagger">
                {activeStores.map((store) => {
                  const typeInfo = getTypeInfo(store.type);
                  return (
                    <div 
                      key={store.id} 
                      className="cgm-ai-card-interactive group !p-0 !overflow-hidden"
                      data-testid={`store-${store.id}`}
                      onClick={() => navigate(`/stores/${store.id}`)}
                    >
                      {/* Gradient Header with animated flow */}
                      <div className={`h-20 relative bg-gradient-to-br ${typeInfo.color} cgm-flow overflow-hidden`}>
                        {/* Subtle grid pattern */}
                        <div className="absolute inset-0 opacity-30" style={{
                          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                          backgroundSize: '20px 20px'
                        }}></div>
                        {/* Active Badge */}
                        <div className="absolute top-3 end-3 z-10">
                          <span className="px-2.5 py-1 bg-white/20 backdrop-blur-md text-white text-xs font-bold rounded-full flex items-center gap-1.5 border border-white/30 shadow-lg">
                            <CheckCircle className="h-3 w-3" />
                            {language === 'ar' ? 'فعال' : 'Active'}
                          </span>
                        </div>
                        
                        {/* Store Icon */}
                        <div className="absolute -bottom-6 start-5 z-10">
                          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-xl border-4 group-hover:scale-110 group-hover:-rotate-3 transition-all duration-500 ${
                            isDarkMode ? 'border-[#131B2C] bg-gradient-to-br from-[#1E293B] to-[#131B2C]' : 'border-white bg-gradient-to-br from-white to-slate-50'
                          }`}>
                            {store.logo ? (
                              <img src={store.logo} alt={store.name} className="w-full h-full rounded-xl object-cover" />
                            ) : (
                              <typeInfo.icon className={`h-7 w-7 ${isDarkMode ? 'text-[#38BDF8]' : 'text-[#0EA5E9]'}`} />
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="p-5 pt-9">
                        {/* Store Name & Type */}
                        <h3 className={`text-base font-black truncate mb-1 ${isDarkMode ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Cairo, sans-serif' }}>
                          {language === 'ar' ? store.name : (store.nameEn || store.name)}
                        </h3>
                        <p className="text-xs text-[#38BDF8] font-bold mb-3 uppercase tracking-wider">
                          {language === 'ar' ? typeInfo.name : typeInfo.nameEn}
                        </p>
                        
                        {/* Creation Date */}
                        <div className={`flex items-center gap-2 text-xs mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          <Calendar className="h-3.5 w-3.5" />
                          <span>{language === 'ar' ? 'تأسس في' : 'Founded'}</span>
                          <span className="font-semibold">
                            {new Date(store.created_at).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>

                        {/* Manage Button */}
                        <button 
                          className="cgm-cta-primary w-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/stores/${store.id}`);
                          }}
                          data-testid={`view-${store.id}`}
                        >
                          <Settings className="h-4 w-4" />
                          {language === 'ar' ? 'إدارة المتجر' : 'Manage Store'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Rejected Stores Tab Content */}
        {activeTab === 'rejected' && (
          <section>
            {rejectedStores.length === 0 ? (
              <div className="cgm-ai-card text-center !py-16">
                <div className="cgm-icon-glow !w-16 !h-16 !p-4 inline-flex mb-4 cgm-float"><XCircle className="h-7 w-7 text-white" /></div>
                <p className="text-lg mb-2 font-black cgm-text-gradient-animated" style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {language === 'ar' ? 'لا توجد متاجر مرفوضة' : 'No rejected stores'}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {language === 'ar' ? 'المتاجر المرفوضة ستظهر هنا' : 'Rejected stores will appear here'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {rejectedStores.map((store) => {
                  const typeInfo = getTypeInfo(store.type);
                  return (
                    <div 
                      key={store.id} 
                      className={`rounded-xl border-2 overflow-hidden ${
                        isDarkMode 
                          ? 'bg-[#131B2C] border-red-500/30' 
                          : 'bg-white border-red-200 shadow-sm'
                      }`}
                      data-testid={`rejected-${store.id}`}
                    >
                      <div className={`p-4 sm:p-5 ${isDarkMode ? 'bg-red-500/10' : 'bg-red-50'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${typeInfo.color} flex items-center justify-center`}>
                              <typeInfo.icon className="h-6 w-6 text-white" />
                            </div>
                            <div>
                              <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                                {language === 'ar' ? store.name : (store.nameEn || store.name)}
                              </h3>
                              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {language === 'ar' ? typeInfo.name : typeInfo.nameEn}
                              </p>
                              {store.store_uid && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigator.clipboard.writeText(store.store_uid);
                                    toast.success(language === 'ar' ? 'تم نسخ الرقم التعريفي' : 'Store ID copied');
                                  }}
                                  className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-md text-[10px] font-mono tracking-wider transition-colors ${
                                    isDarkMode
                                      ? 'bg-white/5 hover:bg-white/10 text-gray-300 border border-white/10'
                                      : 'bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200'
                                  }`}
                                  data-testid={`store-card-uid-${store.id}`}
                                  title={language === 'ar' ? 'انقر للنسخ' : 'Click to copy'}
                                >
                                  <span className="opacity-60">{language === 'ar' ? 'ID:' : 'ID:'}</span>
                                  <span dir="ltr">{store.store_uid}</span>
                                </button>
                              )}
                            </div>
                          </div>
                          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-red-500 text-white">
                            <XCircle className="h-4 w-4" />
                            {language === 'ar' ? 'مرفوض' : 'Rejected'}
                          </span>
                        </div>
                      </div>

                      <div className={`p-4 sm:p-5 ${isDarkMode ? 'border-t border-white/10' : ''}`}>
                        {/* Rejection Reason */}
                        {store.rejection_reason && (
                          <div className={`p-4 rounded-xl border mb-4 ${
                            isDarkMode ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'
                          }`}>
                            <div className="flex items-start gap-3">
                              <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className={`font-bold mb-1 ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
                                  {language === 'ar' ? 'سبب الرفض' : 'Rejection Reason'}
                                </p>
                                <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>{store.rejection_reason}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Store Details */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                              {language === 'ar' ? 'نوع النشاط' : 'Business Type'}
                            </p>
                            <p className={`font-semibold text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {store.business_type === 'company' 
                                ? (language === 'ar' ? 'شركة' : 'Company')
                                : (language === 'ar' ? 'شخصي' : 'Personal')}
                            </p>
                          </div>
                          <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                              {language === 'ar' ? 'تاريخ الطلب' : 'Request Date'}
                            </p>
                            <p className={`font-semibold text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {new Date(store.created_at).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US')}
                            </p>
                          </div>
                          <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                              {language === 'ar' ? 'تاريخ الرفض' : 'Rejection Date'}
                            </p>
                            <p className={`font-semibold text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {store.rejected_at 
                                ? new Date(store.rejected_at).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US')
                                : '-'}
                            </p>
                          </div>
                        </div>

                        {/* Action to view rejection reason */}
                        <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-white/10' : ''}`}>
                          <Button 
                            onClick={() => setSelectedRejectedStore(store)}
                            variant="outline"
                            className={`w-full ${isDarkMode ? 'border-red-500/50 text-red-400 hover:bg-red-500/10' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                            data-testid={`view-rejection-reason-${store.id}`}
                          >
                            <AlertTriangle className="h-4 w-4 me-2" />
                            {language === 'ar' ? 'أسباب الرفض' : 'Rejection Reasons'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Suspended Stores Tab Content */}
        {activeTab === 'suspended' && (
          <section>
            {suspendedStores.length === 0 ? (
              <div className="cgm-ai-card text-center !py-16">
                <div className="cgm-icon-glow !w-16 !h-16 !p-4 inline-flex mb-4 cgm-float"><AlertTriangle className="h-7 w-7 text-white" /></div>
                <p className="text-lg mb-2 font-black cgm-text-gradient-animated" style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {language === 'ar' ? 'لا توجد متاجر موقوفة' : 'No suspended stores'}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {language === 'ar' ? 'المتاجر الموقوفة ستظهر هنا' : 'Suspended stores will appear here'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {suspendedStores.map((store) => {
                  const typeInfo = getTypeInfo(store.type);
                  return (
                    <div 
                      key={store.id} 
                      className={`rounded-2xl border-2 overflow-hidden opacity-75 ${
                        isDarkMode 
                          ? 'bg-[#131B2C] border-gray-600' 
                          : 'bg-white border-gray-300 shadow-md'
                      }`}
                    >
                      {/* Store Header */}
                      <div className={`p-4 sm:p-5 border-b ${
                        isDarkMode ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'
                      }`}>
                        <div className="flex items-center gap-3 sm:gap-4">
                          {store.logo ? (
                            <img src={store.logo} alt="" className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl object-cover grayscale" />
                          ) : (
                            <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br ${typeInfo.color} flex items-center justify-center text-white grayscale`}>
                              {(() => { const Icon = typeInfo.icon; return <Icon className="h-7 w-7 sm:h-8 sm:w-8" />; })()}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className={`font-bold text-base sm:text-lg truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {language === 'ar' ? store.name : (store.nameEn || store.name)}
                            </h3>
                            <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                              {language === 'ar' ? typeInfo.name : typeInfo.nameEn}
                            </p>
                          </div>
                          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                            isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-700'
                          }`}>
                            <AlertTriangle className="h-4 w-4" />
                            <span className="text-xs font-medium">
                              {language === 'ar' ? 'موقوف' : 'Suspended'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Store Body */}
                      <div className="p-4 sm:p-5">
                        {/* Suspension Reason */}
                        {store.suspension_reason && (
                          <div className={`mb-4 p-3 border rounded-lg ${
                            isDarkMode ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'
                          }`}>
                            <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-red-400' : 'text-red-800'}`}>
                              {language === 'ar' ? 'سبب الإيقاف:' : 'Suspension Reason:'}
                            </p>
                            <p className={`text-sm ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>{store.suspension_reason}</p>
                          </div>
                        )}

                        {/* Store Stats */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                              {language === 'ar' ? 'نوع النشاط' : 'Business Type'}
                            </p>
                            <p className={`font-semibold text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {store.business_type === 'company' 
                                ? (language === 'ar' ? 'شركة' : 'Company')
                                : (language === 'ar' ? 'شخصي' : 'Personal')}
                            </p>
                          </div>
                          <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                            <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                              {language === 'ar' ? 'تاريخ الإيقاف' : 'Suspended Date'}
                            </p>
                            <p className={`font-semibold text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {store.suspended_at 
                                ? new Date(store.suspended_at).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US')
                                : new Date(store.updated_at || store.created_at).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US')}
                            </p>
                          </div>
                        </div>

                        {/* Contact Support */}
                        <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-white/10' : ''}`}>
                          <p className={`text-xs text-center mb-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            {language === 'ar' 
                              ? 'للاستفسار عن سبب الإيقاف، يرجى التواصل مع الدعم'
                              : 'For inquiries about suspension, please contact support'}
                          </p>
                          <Button 
                            variant="outline"
                            className={`w-full ${isDarkMode ? 'border-gray-600 text-gray-400' : 'border-gray-400 text-gray-600'}`}
                          >
                            {language === 'ar' ? 'تواصل مع الدعم' : 'Contact Support'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Edit Requests Tab Content */}
        {activeTab === 'edit_requests' && (
          <section>
            {editRequests.filter(r => r.status === 'pending').length === 0 ? (
              <div className="cgm-ai-card text-center !py-16">
                <div className="cgm-icon-glow !w-16 !h-16 !p-4 inline-flex mb-4 cgm-float"><FileEdit className="h-7 w-7 text-white" /></div>
                <p className="text-lg mb-2 font-black cgm-text-gradient-animated" style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {language === 'ar' ? 'لا توجد طلبات تعديل' : 'No edit requests'}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {language === 'ar' ? 'ستظهر هنا طلبات التعديل من الإدارة' : 'Admin edit requests will appear here'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {editRequests.filter(r => r.status === 'pending').map((request) => (
                  <div 
                    key={request.id} 
                    className={`rounded-xl border-2 overflow-hidden ${
                      isDarkMode 
                        ? 'bg-[#131B2C] border-orange-500/30' 
                        : 'bg-white border-orange-200 shadow-sm'
                    }`}
                    data-testid={`edit-request-card-${request.id}`}
                  >
                    <div className={`p-4 sm:p-5 ${isDarkMode ? 'bg-orange-500/10' : 'bg-orange-50'}`}>
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center">
                            <FileEdit className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                              {language === 'ar' ? request.store_name : request.store_name_en}
                            </h3>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {EDIT_REASONS[request.reason]?.[language] || request.reason}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-3 py-1.5 bg-orange-500 text-white text-xs font-bold rounded-full">
                            {language === 'ar' ? 'بانتظار التعديل' : 'Pending'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 sm:p-5">
                      {/* Message from Admin */}
                      {request.message && (
                        <div className={`mb-4 p-4 rounded-xl border ${
                          isDarkMode ? 'bg-orange-500/10 border-orange-500/30' : 'bg-orange-50 border-orange-200'
                        }`}>
                          <div className="flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className={`font-bold mb-1 ${isDarkMode ? 'text-orange-400' : 'text-orange-700'}`}>
                                {language === 'ar' ? 'رسالة الإدارة:' : 'Admin Message:'}
                              </p>
                              <p className={`text-sm ${isDarkMode ? 'text-orange-300' : 'text-orange-600'}`}>{request.message}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Request Info */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                          <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            {language === 'ar' ? 'تاريخ الطلب' : 'Request Date'}
                          </p>
                          <p className={`font-semibold text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {new Date(request.created_at).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US')}
                          </p>
                        </div>
                        <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                          <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                            {language === 'ar' ? 'من الإدارة' : 'From Admin'}
                          </p>
                          <p className={`font-semibold text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {request.admin_email || 'admin@cloudgm.com'}
                          </p>
                        </div>
                      </div>

                      {/* Action Button */}
                      <Button
                        onClick={() => openEditRequestModal(request)}
                        className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
                        data-testid={`open-edit-modal-${request.id}`}
                      >
                        <FileEdit className="h-4 w-4 me-2" />
                        {language === 'ar' ? 'عرض وتعديل المتجر' : 'View & Edit Store'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>

      {/* Edit Request Modal - Full Store Edit Form */}
      {selectedEditRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-request-modal">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Modal Header */}
            <div className="p-5 border-b bg-gradient-to-r from-orange-50 to-amber-50 flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-xl">
                  <FileEdit className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">
                    {language === 'ar' ? 'تعديل المتجر' : 'Edit Store'}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {language === 'ar' ? selectedEditRequest.store_name : selectedEditRequest.store_name_en}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => { setSelectedEditRequest(null); setEditFormData({}); }}
                className="p-2 hover:bg-orange-200 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {/* Request Details */}
            <div className="p-5 space-y-6">
              {/* Admin Request Info Card */}
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-orange-800">
                        {language === 'ar' ? 'سبب طلب التعديل:' : 'Edit Request Reason:'}
                      </span>
                      <span className="text-xs bg-orange-200 text-orange-800 px-2 py-1 rounded-full">
                        {EDIT_REASONS[selectedEditRequest.reason]?.[language] || selectedEditRequest.reason}
                      </span>
                    </div>
                    {selectedEditRequest.message && (
                      <p className="text-sm text-orange-700 bg-white p-3 rounded-lg border border-orange-100">
                        "{selectedEditRequest.message}"
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Step 1: Store Basic Info */}
              <div className="border border-gray-200 rounded-xl p-5">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 bg-[#1E9BD9] text-white rounded-full flex items-center justify-center text-sm">1</span>
                  {language === 'ar' ? 'معلومات المتجر الأساسية' : 'Basic Store Information'}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Store Name Arabic */}
                  <div>
                    <Label className="text-gray-700 font-medium">
                      {language === 'ar' ? 'اسم المتجر (عربي) *' : 'Store Name (Arabic) *'}
                    </Label>
                    <Input
                      value={editFormData.name || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                      className="mt-2"
                      dir="rtl"
                      data-testid="edit-store-name-ar"
                    />
                  </div>

                  {/* Store Name English */}
                  <div>
                    <Label className="text-gray-700 font-medium">
                      {language === 'ar' ? 'اسم المتجر (إنجليزي) *' : 'Store Name (English) *'}
                    </Label>
                    <Input
                      value={editFormData.nameEn || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, nameEn: e.target.value })}
                      className="mt-2"
                      dir="ltr"
                      data-testid="edit-store-name-en"
                    />
                  </div>
                </div>
              </div>

              {/* Step 2: Descriptions */}
              <div className="border border-gray-200 rounded-xl p-5">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 bg-[#1E9BD9] text-white rounded-full flex items-center justify-center text-sm">2</span>
                  {language === 'ar' ? 'وصف المتجر' : 'Store Description'}
                </h3>
                
                <div className="space-y-4">
                  {/* Description Arabic */}
                  <div>
                    <Label className="text-gray-700 font-medium">
                      {language === 'ar' ? 'الوصف (عربي)' : 'Description (Arabic)'}
                    </Label>
                    <textarea
                      value={editFormData.description || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                      className="mt-2 w-full px-3 py-2 border border-gray-200 rounded-lg resize-none h-24 focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]/20 focus:border-[#1E9BD9]"
                      dir="rtl"
                      data-testid="edit-store-description-ar"
                    />
                  </div>

                  {/* Description English */}
                  <div>
                    <Label className="text-gray-700 font-medium">
                      {language === 'ar' ? 'الوصف (إنجليزي)' : 'Description (English)'}
                    </Label>
                    <textarea
                      value={editFormData.descriptionEn || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, descriptionEn: e.target.value })}
                      className="mt-2 w-full px-3 py-2 border border-gray-200 rounded-lg resize-none h-24 focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]/20 focus:border-[#1E9BD9]"
                      dir="ltr"
                      data-testid="edit-store-description-en"
                    />
                  </div>
                </div>
              </div>

              {/* Step 3: Documents (if company) */}
              {selectedEditRequest.storeData?.business_type === 'company' && (
                <div className="border border-gray-200 rounded-xl p-5">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <span className="w-6 h-6 bg-[#1E9BD9] text-white rounded-full flex items-center justify-center text-sm">3</span>
                    {language === 'ar' ? 'المستندات والمرفقات' : 'Documents & Attachments'}
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Current License */}
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        {language === 'ar' ? 'الرخصة التجارية الحالية' : 'Current Trade License'}
                      </p>
                      {selectedEditRequest.storeData?.license_document ? (
                        <a 
                          href={selectedEditRequest.storeData.license_document} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-[#1E9BD9] hover:underline text-sm"
                        >
                          <File className="h-4 w-4" />
                          {language === 'ar' ? 'عرض المستند' : 'View Document'}
                        </a>
                      ) : (
                        <p className="text-sm text-gray-400">{language === 'ar' ? 'لا يوجد' : 'Not uploaded'}</p>
                      )}
                    </div>

                    {/* Upload New License */}
                    <div className="p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#1E9BD9] transition-colors">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        {language === 'ar' ? 'رفع رخصة جديدة' : 'Upload New License'}
                      </p>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setEditFormData({ ...editFormData, newLicense: e.target.files[0] })}
                        className="w-full text-sm"
                      />
                      {editFormData.newLicense && (
                        <p className="text-xs text-green-600 mt-1">{editFormData.newLicense.name}</p>
                      )}
                    </div>

                    {/* Authorization if not owner */}
                    {selectedEditRequest.storeData?.is_owner === false && (
                      <>
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <p className="text-sm font-medium text-gray-700 mb-2">
                            {language === 'ar' ? 'رسالة التخويل الحالية' : 'Current Authorization'}
                          </p>
                          {selectedEditRequest.storeData?.authorization_document ? (
                            <a 
                              href={selectedEditRequest.storeData.authorization_document} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-[#1E9BD9] hover:underline text-sm"
                            >
                              <File className="h-4 w-4" />
                              {language === 'ar' ? 'عرض المستند' : 'View Document'}
                            </a>
                          ) : (
                            <p className="text-sm text-gray-400">{language === 'ar' ? 'لا يوجد' : 'Not uploaded'}</p>
                          )}
                        </div>

                        <div className="p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#1E9BD9] transition-colors">
                          <p className="text-sm font-medium text-gray-700 mb-2">
                            {language === 'ar' ? 'رفع رسالة تخويل جديدة' : 'Upload New Authorization'}
                          </p>
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => setEditFormData({ ...editFormData, newAuthorization: e.target.files[0] })}
                            className="w-full text-sm"
                          />
                          {editFormData.newAuthorization && (
                            <p className="text-xs text-green-600 mt-1">{editFormData.newAuthorization.name}</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Info Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Eye className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-blue-700">
                      {language === 'ar' 
                        ? 'بعد حفظ التعديلات، سيتم إرسالها للإدارة للمراجعة وسيختفي طلب التعديل من القائمة.'
                        : 'After saving, your changes will be sent for admin review and the edit request will be removed from the list.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t bg-gray-50 flex flex-col sm:flex-row gap-3 sticky bottom-0">
              <Button 
                variant="outline" 
                className="flex-1 order-2 sm:order-1"
                onClick={() => { setSelectedEditRequest(null); setEditFormData({}); }}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button 
                onClick={saveStoreEdits}
                disabled={savingEdit}
                className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white order-1 sm:order-2"
                data-testid="save-edit-request-btn"
              >
                {savingEdit ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Save className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'حفظ وإرسال التعديلات' : 'Save & Submit Changes'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Reason Modal */}
      {selectedRejectedStore && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="rejection-reason-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            {/* Modal Header */}
            <div className="p-5 border-b bg-gradient-to-r from-red-50 to-red-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-xl">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">
                    {language === 'ar' ? 'أسباب الرفض' : 'Rejection Reasons'}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {language === 'ar' ? selectedRejectedStore.name : (selectedRejectedStore.nameEn || selectedRejectedStore.name)}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedRejectedStore(null)}
                className="p-2 hover:bg-red-200 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-5 space-y-4">
              {/* Rejection Reason */}
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-bold text-red-700 mb-2">
                      {language === 'ar' ? 'سبب الرفض:' : 'Rejection Reason:'}
                    </p>
                    <p className="text-red-600 text-sm leading-relaxed">
                      {selectedRejectedStore.rejection_reason || (language === 'ar' ? 'لم يتم تحديد سبب الرفض' : 'No rejection reason specified')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Store Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-xs text-gray-500 mb-1">
                    {language === 'ar' ? 'تاريخ الطلب' : 'Request Date'}
                  </p>
                  <p className="font-semibold text-sm text-gray-700">
                    {new Date(selectedRejectedStore.created_at).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US')}
                  </p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg text-center">
                  <p className="text-xs text-gray-500 mb-1">
                    {language === 'ar' ? 'تاريخ الرفض' : 'Rejection Date'}
                  </p>
                  <p className="font-semibold text-sm text-gray-700">
                    {selectedRejectedStore.rejected_at 
                      ? new Date(selectedRejectedStore.rejected_at).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US')
                      : '-'}
                  </p>
                </div>
              </div>

              {/* Help Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-blue-700">
                      {language === 'ar' 
                        ? 'إذا كنت تعتقد أن هذا الرفض غير صحيح، يرجى التواصل مع الدعم الفني.'
                        : 'If you believe this rejection is incorrect, please contact support.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t bg-gray-50 flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setSelectedRejectedStore(null)}
              >
                {language === 'ar' ? 'إغلاق' : 'Close'}
              </Button>
              <Button 
                onClick={() => {
                  setSelectedRejectedStore(null);
                  navigate('/stores/create');
                }}
                className="flex-1 bg-gradient-to-r from-[#1E9BD9] to-[#0369A1] text-white"
              >
                <Plus className="h-4 w-4 me-2" />
                {language === 'ar' ? 'إنشاء متجر جديد' : 'Create New Store'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
